import { Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTableDataSource } from "@angular/material/table";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { SVG_ICONS } from "../../../models/icons";
import { SanitizerPipe } from "../../../pipes/sanitizer.pipe";
import { GlobalService } from "../../../serivces/global.service";
import { Router } from "@angular/router";
import { MasterService } from "../../../serivces/master.service";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { forkJoin } from "rxjs";
interface UserAccessItem {
  RegionID: number;
  AreaID: number;
  CenterID: number;
  Region: { RegionID: number; RegionName: string } | null;
  Area: { AreaID: number; AreaName: string } | null;
  Center: { CenterID: number; CenterName: string; Address: string };
}

interface RegionGroup {
  RegionID: number;
  RegionName: string;
  AreaCount: number;
  CenterCount: number;
  centerDetails: any[];
}

interface AreaGroup {
  AreaID: number;
  AreaName: string;
  CenterCount: number;
  ClassCount: string;
  centerDetails: any[];
}

@Component({
  selector: "app-center-select-page",
  standalone: true,
  imports: [CommonModule, SanitizerPipe, SharedModule],
  templateUrl: "./center-select-page.component.html",
  styleUrl: "./center-select-page.component.scss",
  animations: [
    trigger("detailExpand", [
      state("collapsed, void", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
    ]),
  ],
})
export class CenterSelectPageComponent implements OnInit, OnDestroy {
  // --- Services and Properties ---
  private globalService = inject(GlobalService);
  private masterService = inject(MasterService);
  private router = inject(Router);

  // --- Lifecycle Hooks ---
  ngOnInit(): void {
    this.globalService.setIsShowFalse();
    this.globalService.setIsLoggedInTrue();
    this.userName = sessionStorage.getItem("UserLoginID") || "";
    this.loadInitialData();
  }

  //#endregion
  Counts: any;
  readonly SVG_ICONS = SVG_ICONS;
  userName = "";
  selectedTab: "region" | "area" = "region";
  regionDataSource = new MatTableDataSource<RegionGroup>();
  areaDataSource = new MatTableDataSource<AreaGroup>();
  displayedColumns: string[] = ["card"];
  centerList: any[] = [];
  expandedElement: any | null;
  totalCenter = 0;
  totalTrainer = 0;
  totalStudent = 0;
  Pagelength = 0;
  userTypeID = +sessionStorage.getItem("UserTypeID")!;

  @ViewChild("regionPaginator") regionPaginator!: MatPaginator;
  @ViewChild("areaPaginator") areaPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  //#endregion

  loadInitialData() {
    const userID = +sessionStorage.getItem("UserID")!;
    if (!userID) return;

    this.masterService.getUserAccess(userID).subscribe((res: any) => {
      this.Counts = res.Counts;
      if (res && Array.isArray(res.Table)) {
        const accessData = res.Table;

        const groupedByRegion = this.groupDataByRegion(accessData);
        this.regionDataSource.data = groupedByRegion;

        const groupedByArea = this.groupDataByArea(accessData);
        this.areaDataSource.data = groupedByArea;

        this.onTabChange(this.selectedTab, true);
      }
    });
  }

  private groupDataByRegion(data: any[]): RegionGroup[] {
    const regionMap = new Map<number, any>();

    data.forEach(item => {
      if (!item.RegionID || !item.Region) return;

      if (!regionMap.has(item.RegionID)) {
        regionMap.set(item.RegionID, {
          RegionID: item.RegionID,
          RegionName: item.Region.RegionName,
          uniqueAreaIDs: new Set<number>(),
          centers: new Map<number, any>(),
          totalClasses: 0,
          totalTrainers: 0,
          totalStudents: 0,
        });
      }

      const regionGroup = regionMap.get(item.RegionID);

      if (item.AreaID) {
        regionGroup.uniqueAreaIDs.add(item.AreaID);
      }

      if (item.CenterID && item.Center) {
        // Convert counts to numbers and ensure they're valid numbers
        const classCount = isNaN(Number(item.classCount)) ? 0 : Number(item.classCount);
        const trainerCount = isNaN(Number(item.trainerCount)) ? 0 : Number(item.trainerCount);
        const studentCount = isNaN(Number(item.studentCount)) ? 0 : Number(item.studentCount);

        if (!regionGroup.centers.has(item.CenterID)) {
          // Initialize center with counts from the item or 0 if not available
          const centerData = {
            ...item.Center,
            classCount: classCount,
            trainerCount: trainerCount,
            studentCount: studentCount,
          };

          regionGroup.centers.set(item.CenterID, centerData);

          // Update region totals
          regionGroup.totalClasses += classCount;
          regionGroup.totalTrainers += trainerCount;
          regionGroup.totalStudents += studentCount;
        }
      }
    });

    // Convert the map values to an array of RegionGroup objects
    const result = Array.from(regionMap.values()).map(group => ({
      RegionID: group.RegionID,
      RegionName: group.RegionName,
      AreaCount: group.uniqueAreaIDs.size,
      CenterCount: group.centers.size,
      TotalClasses: group.totalClasses,
      TotalTrainers: group.totalTrainers,
      TotalStudents: group.totalStudents,
      centerDetails: Array.from(group.centers.values()),
    }));

    return result;
  }

  private groupDataByArea(data: any[]): AreaGroup[] {
    const areaMap = new Map<number, any>();

    data.forEach(item => {
      if (!item.AreaID || !item.Area) return;

      if (!areaMap.has(item.AreaID)) {
        areaMap.set(item.AreaID, {
          AreaID: item.AreaID,
          AreaName: item.Area.AreaName,
          centers: new Map<number, any>(),
          totalClasses: 0,
          totalTrainers: 0,
          totalStudents: 0,
        });
      }

      const areaGroup = areaMap.get(item.AreaID);

      if (item.CenterID && item.Center) {
        if (!areaGroup.centers.has(item.CenterID)) {
          // Initialize center with counts from the item or 0 if not available
          const centerData = {
            ...item.Center,
            classCount: item.classCount || 0,
            trainerCount: item.trainerCount || 0,
            studentCount: item.studentCount || 0,
          };

          areaGroup.centers.set(item.CenterID, centerData);

          // Update area totals
          areaGroup.totalClasses += Number(centerData.classCount) || 0;
          areaGroup.totalTrainers += Number(centerData.trainerCount) || 0;
          areaGroup.totalStudents += Number(centerData.studentCount) || 0;
        }
      }
    });

    return Array.from(areaMap.values()).map(group => ({
      AreaID: group.AreaID,
      AreaName: group.AreaName,
      CenterCount: group.centers.size,
      ClassCount: group.totalClasses,
      TrainerCount: group.totalTrainers,
      StudentCount: group.totalStudents,
      centerDetails: Array.from(group.centers.values()),
    }));
  }

  // --- UI Interaction ---
  onTabChange(tab: "region" | "area", isInitialLoad = false) {
    if (!isInitialLoad && this.selectedTab === tab) return;
    this.selectedTab = tab;
    this.expandedElement = null;
    this.centerList = [];
    this.setupPaginatorAndSort();
  }

  toggleRegion(row: any) {
    this.expandedElement = this.expandedElement === row ? null : row;
    if (this.expandedElement === row) {
      this.getCenterSByRegionID(row?.RegionID);
    }
  }

  toggleArea(row: any) {
    this.expandedElement = this.expandedElement === row ? null : row;
    if (this.expandedElement === row) {
      this.getCenterSByAreaID(row?.AreaID);
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    const dataSource = this.selectedTab === "region" ? this.regionDataSource : this.areaDataSource;
    dataSource.filter = filterValue;
    if (dataSource.paginator) {
      dataSource.paginator.firstPage();
    }
  }

  private setupPaginatorAndSort() {
    setTimeout(() => {
      if (this.selectedTab === "region") {
        this.Pagelength = this.regionDataSource.data.length;
        this.regionDataSource.paginator = this.regionPaginator;
        this.regionDataSource.sort = this.sort;
      } else if (this.selectedTab === "area") {
        this.Pagelength = this.areaDataSource.data.length;
        this.areaDataSource.paginator = this.areaPaginator;
        this.areaDataSource.sort = this.sort;
      }
    });
  }

  getCenterSByRegionID(regionID: number) {
    const userID = +sessionStorage.getItem("UserID")!;
    const centersInRegion$ = this.masterService.getCenterSByRegionID(regionID);
    const userAccess$ = this.masterService.getUserAccess(userID);

    forkJoin({ centers: centersInRegion$, access: userAccess$ }).subscribe(({ centers, access }) => {
      const userAccessList = access.Table || [];
      const allowedCenterIDs = new Set(userAccessList.map((rule: any) => rule.CenterID).filter((id: any) => id != null));
      const allCentersInRegion = centers?.data || [];
      const filteredCenters = allCentersInRegion.filter((center: any) => allowedCenterIDs.has(center.CenterID));
      this.centerList = filteredCenters;
    });
  }

  getCenterSByAreaID(areaID: number) {
    const userID = +sessionStorage.getItem("UserID")!;
    const centersInArea$ = this.masterService.getCenterSByAreaID(areaID);
    const userAccess$ = this.masterService.getUserAccess(userID);

    forkJoin({ centers: centersInArea$, access: userAccess$ }).subscribe(({ centers, access }) => {
      const userAccessList = access.Table || [];
      const allowedCenterIDs = new Set(userAccessList.map((rule: any) => rule.CenterID).filter((id: any) => id != null));
      const allCentersInArea = centers?.data?.centers || [];
      const filteredCenters = allCentersInArea.filter((center: any) => allowedCenterIDs.has(center.CenterID));
      this.centerList = filteredCenters;
    });
  }

  async onClick(centerId: number) {
    sessionStorage.setItem("selectedCenter", JSON.stringify([centerId]));
    await this.router.navigate(["dashboard"]);
    await window.location.reload();
  }

  async onClickRegion(region: any) {
    const centerIDs = region?.centerDetails?.map((c: any) => c.CenterID) || [];
    sessionStorage.setItem("selectedCenter", JSON.stringify(centerIDs));
    await this.router.navigate(["dashboard"]);
    window.location.reload();
  }

  async onClickArea(area: any) {
    const centerIDs = area?.centerDetails?.map((c: any) => c.CenterID) || [];
    sessionStorage.setItem("selectedCenter", JSON.stringify(centerIDs));
    await this.router.navigate(["dashboard"]);
    window.location.reload();
  }

  onCreateCenter() {
    this.router.navigate(["create-center"]);
  }

  async onDashboard() {
    const allCenterIDs = JSON.parse(sessionStorage.getItem("AllCenterIDs") || "[]");
    sessionStorage.setItem("selectedCenter", JSON.stringify(allCenterIDs));

    await this.router.navigate(["dashboard"]);
    // await window.location.reload();
  }

  ngOnDestroy(): void {
    this.globalService.setIsShowTrue();
  }
}
